import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import FeatherIcon from "feather-icons-react";
import { importAlertState } from "src/redux/actions/alert";
import { importUpdateTransactions } from "src/redux/actions/conciliacion";
import { setUpdateDocuments } from "src/redux/actions/documentostributarios";
import { uploadToS3, getFileSizeInMB } from "src/utils/s3Upload";
import {
  ENTITY_ATTACHMENT_MAX_MB,
  DeleteEntityAttachment,
  fetchEntityAttachmentDownloadUrl,
  fetchEntityAttachmentUploadUrl,
  SaveEntityAttachment,
} from "src/graphql/entityAttachments";
import "../importProviders/style.scss";
import "./reconcileAttachments.scss";
import DeleteAttachmentModal from "./DeleteAttachmentModal";

const ReconcileAttachments = ({
  data,
  dataType,
  findDocumentById,
  getTransaction,
  companyId,
}) => {
  const dispatch = useDispatch();
  const { executeMutation: saveAttachment } = SaveEntityAttachment();
  const { executeMutation: deleteAttachment } = DeleteEntityAttachment();

  const entityId = data?.id || data?._id;
  const attachedFiles = data?.attachedFiles || [];

  const [documentName, setDocumentName] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadingFileName, setDownloadingFileName] = useState(null);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const validateFile = (selected) => {
    if (!selected) return false;
    const fileSizeMB = getFileSizeInMB(selected.size);
    if (fileSizeMB > ENTITY_ATTACHMENT_MAX_MB) {
      setError(
        `* El archivo excede el tamaño máximo de ${ENTITY_ATTACHMENT_MAX_MB} MB (${fileSizeMB.toFixed(2)} MB)`
      );
      setFile(null);
      setDocumentName("");
      return false;
    }
    setFile(selected);
    setDocumentName(selected.name);
    setError("");
    return true;
  };

  const selectFile = (selected) => {
    validateFile(selected);
  };

  const hasDraggedFiles = (e) =>
    Array.from(e.dataTransfer?.types || []).includes("Files");

  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!hasDraggedFiles(e)) return;
    dragCounterRef.current += 1;
    setIsDragOver(true);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasDraggedFiles(e)) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  };

  const resetDragState = () => {
    dragCounterRef.current = 0;
    setIsDragOver(false);
  };

  const refetchEntity = async () => {
    if (dataType === "transaction") {
      await getTransaction?.reexecuteQuery?.({
        requestPolicy: "network-only",
        variables: { id: entityId },
      });
      dispatch(importUpdateTransactions(true));
    } else {
      await findDocumentById?.reexecuteQuery?.({ requestPolicy: "network-only" });
      dispatch(setUpdateDocuments({ value: true }));
    }
  };

  const uploadFile = async (fileToUpload) => {
    if (!fileToUpload || !entityId || !companyId) {
      setError("* Debe seleccionar un archivo");
      return;
    }

    if (!validateFile(fileToUpload)) return;

    setLoading(true);
    setError("");

    try {
      const entityType = dataType === "transaction" ? "TRANSACTION" : "DOCUMENT";
      const payload = await fetchEntityAttachmentUploadUrl({
        entityId,
        entityType,
        fileName: fileToUpload.name,
        fileType: fileToUpload.type || "application/octet-stream",
      });

      await uploadToS3(fileToUpload, payload.uploadUrl);

      const result = await saveAttachment({
        input: {
          entityId,
          entityType,
          fileId: payload.fileId,
          originalFileName: fileToUpload.name,
        },
      });

      if (result.error) {
        throw new Error(result.error.message || "Error al guardar el archivo");
      }

      dispatch(
        importAlertState({
          content: "Archivo cargado correctamente.",
          state: "success",
          duration: 4,
        })
      );

      setFile(null);
      setDocumentName("");
      await refetchEntity();
    } catch (err) {
      dispatch(
        importAlertState({
          content: err?.message || "Error al cargar el archivo.",
          state: "error",
          duration: 5,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const onHandleUpload = () => uploadFile(file);

  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    resetDragState();

    if (loading) return;

    const dropped = e.dataTransfer?.files?.[0];
    if (!dropped) return;
    await uploadFile(dropped);
  };

  const handleDownload = async (attachment) => {
    if (!attachment?.fileName || !entityId) return;
    setDownloadingFileName(attachment.fileName);
    try {
      const entityType = dataType === "transaction" ? "TRANSACTION" : "DOCUMENT";
      const url = await fetchEntityAttachmentDownloadUrl({
        entityId,
        entityType,
        fileName: attachment.fileName,
      });
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      dispatch(
        importAlertState({
          content: err?.message || "No se pudo descargar el archivo.",
          state: "error",
          duration: 4,
        })
      );
    } finally {
      setDownloadingFileName(null);
    }
  };

  const handleDeleteClick = (attachment) => {
    if (!attachment?.fileName || !entityId) return;
    setAttachmentToDelete(attachment);
  };

  const handleConfirmDelete = async () => {
    const attachment = attachmentToDelete;
    if (!attachment?.fileName || !entityId) return;

    setIsDeleting(true);
    try {
      const entityType = dataType === "transaction" ? "TRANSACTION" : "DOCUMENT";
      const result = await deleteAttachment({
        input: {
          entityId,
          entityType,
          fileName: attachment.fileName,
        },
      });

      if (result.error) {
        throw new Error(result.error.message || "Error al eliminar el archivo");
      }

      dispatch(
        importAlertState({
          content: "Archivo eliminado correctamente.",
          state: "success",
          duration: 4,
        })
      );
      setAttachmentToDelete(null);
      await refetchEntity();
    } catch (err) {
      dispatch(
        importAlertState({
          content: err?.message || "No se pudo eliminar el archivo.",
          state: "error",
          duration: 5,
        })
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const deletingFileName = isDeleting ? attachmentToDelete?.fileName : null;

  return (
    <div
      className={`reconcile-attachments${isDragOver ? " reconcile-attachments--drag-over" : ""}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {isDragOver && (
        <div className="reconcile-attachments__drop-overlay" aria-hidden>
          <FeatherIcon icon="upload-cloud" size={40} />
          <span>Suelta el archivo para cargarlo</span>
        </div>
      )}

      <p className="ip-title">Cargar archivo</p>

      <label
        className={`ip-dropzone${documentName ? " ip-dropzone--selected" : ""}`}
      >
        <input
          type="file"
          className="ip-dropzone__input"
          onChange={(e) => selectFile(e?.target?.files?.[0] || null)}
        />
        <span className="ip-dropzone__icon">
          <FeatherIcon icon={documentName ? "file-text" : "upload-cloud"} size={32} />
        </span>
        {documentName ? (
          <span className="ip-dropzone__filename">{documentName}</span>
        ) : (
          <span className="ip-dropzone__hint">
            Arrastra un archivo a esta pestaña o <u>haz click para seleccionar uno</u>
            <br />
            <span className="reconcile-attachments__limit">Máximo {ENTITY_ATTACHMENT_MAX_MB} MB</span>
          </span>
        )}
      </label>

      {error && <div className="ip-error">{error}</div>}

      <div className="ip-actions">
        <button
          className="ip-btn-primary"
          type="button"
          onClick={onHandleUpload}
          disabled={loading || !file}
        >
          {loading ? (
            <>
              <span className="ip-spinner" />
              Cargando…
            </>
          ) : (
            <>
              <FeatherIcon icon="upload" size={15} />
              {documentName || "Cargar documento"}
            </>
          )}
        </button>
      </div>

      {attachedFiles.length > 0 && (
        <div className="reconcile-attachments__list">
          <p className="reconcile-attachments__list-title">Archivos cargados</p>
          {attachedFiles.map((attachment, index) => (
            <div key={`${attachment.fileName}-${index}`} className="reconcile-attachments__item">
              <div className="reconcile-attachments__item-info">
                <FeatherIcon icon="file-text" size={16} />
                <span className="reconcile-attachments__item-name">
                  {attachment.originalFileName || attachment.fileName}
                </span>
              </div>
              <div className="reconcile-attachments__item-actions">
                <button
                  type="button"
                  className="reconcile-attachments__download-btn"
                  onClick={() => handleDownload(attachment)}
                  disabled={downloadingFileName === attachment.fileName || deletingFileName === attachment.fileName}
                >
                  <FeatherIcon icon="download" size={14} />
                  {downloadingFileName === attachment.fileName ? "Descargando…" : "Descargar"}
                </button>
                <button
                  type="button"
                  className="reconcile-attachments__delete-btn"
                  onClick={() => handleDeleteClick(attachment)}
                  disabled={deletingFileName === attachment.fileName || downloadingFileName === attachment.fileName}
                  title="Eliminar archivo"
                >
                  <FeatherIcon icon="trash-2" size={14} />
                  {deletingFileName === attachment.fileName ? "Eliminando…" : "Eliminar"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <DeleteAttachmentModal
        open={Boolean(attachmentToDelete)}
        onClose={() => setAttachmentToDelete(null)}
        onConfirm={handleConfirmDelete}
        attachment={attachmentToDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default ReconcileAttachments;
